import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../Icon';
import { EVENTS } from '../../constants/events';

/**
 * The free-storybook step of the book-promo funnel (/funnel-auth?variant=book).
 *
 * Laid out after the iOS onboarding's first-story step
 * (PixleyIOS/Pixley-iOS/Onboarding/NOFirstAdventureStep.swift), translated into
 * the funnel's CSS system. What that gets us over a plain option grid:
 *
 *   - left-aligned eyebrow + title with one word in accentDeep (#0065F8)
 *   - the WRITE-YOUR-OWN card first and prominent, ready-mades second, because
 *     iOS treats the custom story as the primary path and the presets as the
 *     shortcut — the reverse of how a settings-style option list reads
 *   - one source of truth: typing clears the picked book, picking clears the
 *     text, and tapping the picked book again returns you to writing
 *   - ready-mades as capsule pills with a cover swatch, selected filling with
 *     the accent gradient
 *
 * Sits between `meet` and `signin`, so the character already exists and the 3D
 * cover mockup can show theirs. That mockup has no iOS counterpart — it's the
 * one thing that makes a *printed* book feel real — so it's kept as the hero.
 *
 * Picking the story is ALL this step does. The shipping address is collected
 * later in the funnel, after checkout, by FunnelBookAddressPage — asking a
 * stranger mid-sign-up for their home address costs more sign-ups than it saves
 * round trips, and by then they're a paying customer.
 */

// The four ready-made books plus the custom slot.
//
// `title`/`blurb` are display-only. The actual story prompt lives on the
// backend (routers/print_orders_router.py FREE_BOOK_PROMPTS, keyed by these
// same `key`s) so the prompts can be tuned without shipping a new bundle —
// and so a hand-crafted request body can't smuggle in an arbitrary prompt.
// `short` is what the pill shows; `title` is the real book title and goes on
// the cover, in the sign-in copy and in the print order. Two full titles per
// row would wrap to three lines each and cost more height than the grid saves.
export const FREE_BOOK_OPTIONS = [
  { key: 'bedtime-stars', title: 'The Sleepy Star', short: 'Sleepy Star', icon: 'bedtime', tint: '#7C5CFF' },
  { key: 'jungle-rescue', title: 'The Great Jungle Rescue', short: 'Jungle Rescue', icon: 'forest', tint: '#34C759' },
  { key: 'moon-mission', title: 'Mission to the Moon', short: 'Moon Mission', icon: 'rocket_launch', tint: '#0A6CFF' },
  { key: 'kindness-machine', title: 'The Kindness Machine', short: 'Kindness', icon: 'volunteer_activism', tint: '#FF6FA5' },
];

export const CUSTOM_BOOK_KEY = 'custom';

// Rotating examples for the empty field. Lifted from the iOS step's approach:
// concrete examples teach the format far better than one static hint.
const PLACEHOLDERS = [
  'A lost baby dragon who needs help getting home',
  'A bedtime trip to a city under the sea',
  'A puppy who is scared of the dark',
  'Making a new friend on the first day of school',
  'A birthday party where everything goes wrong',
];

// One-tap seeds. The chip shows a two-word `label` and drops the full `text`
// into the field, so the strip stays one row while the field still gets a
// properly-formed story idea that can then be edited.
const STORY_IDEAS = [
  { emoji: '🐉', label: 'Dragons', text: 'A lost baby dragon who needs help getting home' },
  { emoji: '🌊', label: 'Under the sea', text: 'An underwater city with a secret' },
  { emoji: '🧁', label: 'Magic bakery', text: 'A bakery where the cakes come alive' },
  { emoji: '🐕', label: 'Brave puppy', text: 'A puppy who is scared of the dark' },
  { emoji: '🚂', label: 'Trains', text: 'A train that runs on imagination' },
];

const MIN_CUSTOM_LENGTH = 8;
const MAX_CUSTOM_LENGTH = 300;

const FunnelBookStep = ({
  childName,
  previewImage,
  bookKey,
  customPrompt,
  onSelectBook,
  onCustomPromptChange,
  onSubmit,
  onTrack,
  itemVariants,
}) => {
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  const name = (childName || '').trim();
  const selected = FREE_BOOK_OPTIONS.find((o) => o.key === bookKey) || null;
  const isCustom = bookKey === CUSTOM_BOOK_KEY;
  const trimmedPrompt = (customPrompt || '').trim();

  // Cycle the examples only while the field is genuinely idle — mid-typing or
  // with a book picked, a moving placeholder is just noise.
  useEffect(() => {
    if (trimmedPrompt || selected) return undefined;
    const id = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % PLACEHOLDERS.length);
    }, 3200);
    return () => clearInterval(id);
  }, [trimmedPrompt, selected]);

  // What the mockup's cover says. Never blank — an empty cover reads as broken
  // rather than as "nothing picked yet".
  const coverTitle = useMemo(() => {
    if (isCustom) return trimmedPrompt ? trimmedPrompt.slice(0, 48) : 'Your Own Story';
    return selected?.title || `${name}'s Big Adventure`;
  }, [isCustom, trimmedPrompt, selected, name]);

  const coverTint = isCustom ? '#FFC83D' : selected?.tint || '#0A6CFF';
  const canContinue = selected ? true : isCustom && trimmedPrompt.length >= MIN_CUSTOM_LENGTH;

  // ── one source of truth (the iOS rule) ───────────────────────────────────
  // Typing is a deliberate switch back to a custom story…
  const writeCustom = (value) => {
    onCustomPromptChange(value.slice(0, MAX_CUSTOM_LENGTH));
    if (bookKey !== CUSTOM_BOOK_KEY) onSelectBook(CUSTOM_BOOK_KEY);
  };

  // …and picking a ready-made clears whatever was typed. Tapping the picked one
  // again returns to writing rather than stranding them on a choice.
  const pickBook = (key) => {
    if (bookKey === key) {
      onSelectBook('');
      return;
    }
    onSelectBook(key);
    onCustomPromptChange('');
  };

  const applyIdea = (text) => {
    onCustomPromptChange(text);
    onSelectBook(CUSTOM_BOOK_KEY);
  };

  const submit = () => {
    onTrack?.(EVENTS.FUNNEL_BOOK_SELECTED, {
      book_key: bookKey,
      is_custom: isCustom,
      custom_prompt_length: isCustom ? trimmedPrompt.length : null,
    });
    onSubmit();
  };

  return (
    <div className="fb-step">
      <FunnelBookStepStyles />

      {/* The reason to keep going — their character, on a real book cover. */}
      <motion.div variants={itemVariants} className="fb-book-stage">
        <div className="fb-book" style={{ '--tint': coverTint }}>
          <span className="fb-book-pages" aria-hidden="true" />
          <span className="fb-book-spine" aria-hidden="true" />
          <div className="fb-book-cover">
            <span className="fb-book-shine" aria-hidden="true" />
            <span className="fb-book-brand">A Pixley Book</span>
            {previewImage ? (
              <img src={previewImage} alt="" className="fb-book-hero" />
            ) : (
              <span className="fb-book-hero fb-book-hero-empty" aria-hidden="true">
                <Icon name="auto_stories" size={44} />
              </span>
            )}
            <span className="fb-book-title">{coverTitle}</span>
            {name && <span className="fb-book-starring">starring {name}</span>}
          </div>
        </div>
        <span className="fb-book-shadow" aria-hidden="true" />
      </motion.div>

      <motion.div variants={itemVariants}>
        <div className="fa-eyebrow">Free gift</div>
        <h1 className="fa-title">
          {name ? <>Pick {name}'s <span className="fb-accent">free</span> storybook</>
                : <>Pick your <span className="fb-accent">free</span> storybook</>}
        </h1>
        <p className="fa-subtitle">
          A real hardcover{name ? `, starring ${name}` : ''} — written, illustrated and posted to
          you free.
        </p>

        {/* Write-your-own first: the iOS step treats the custom story as the
            primary path, with the ready-mades as the shortcut below it. */}
        <div className={`fb-story-card${selected ? ' receded' : ''}`}>
          <div className="fb-story-head">
            <Icon name="auto_awesome" size={13} fill />
            <span>The story</span>
          </div>
          <div className="fb-story-input-wrap">
            {!customPrompt && (
              <AnimatePresence mode="wait">
                <motion.span
                  key={placeholderIndex}
                  className="fb-story-placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  aria-hidden="true"
                >
                  {PLACEHOLDERS[placeholderIndex]}
                </motion.span>
              </AnimatePresence>
            )}
            <textarea
              className="fb-story-input"
              value={customPrompt || ''}
              onChange={(e) => writeCustom(e.target.value)}
              rows={2}
              aria-label="Describe the story you'd like"
            />
          </div>
        </div>
        {!!customPrompt && (
          <div className="fb-story-foot">
            <span className={trimmedPrompt.length >= MAX_CUSTOM_LENGTH ? 'is-max' : ''}>
              {customPrompt.length}/{MAX_CUSTOM_LENGTH}
            </span>
          </div>
        )}

        <div className="fb-idea-strip">
          {STORY_IDEAS.map((idea) => (
            <button
              key={idea.text}
              type="button"
              className={`fb-idea-tag${!selected && trimmedPrompt === idea.text ? ' selected' : ''}`}
              onClick={() => applyIdea(idea.text)}
              title={idea.text}
            >
              <span className="fb-idea-emoji" aria-hidden="true">{idea.emoji}</span>
              {idea.label}
            </button>
          ))}
        </div>

        <div className="fb-divider"><span>or pick one of ours</span></div>

        <div className="fb-book-grid">
          {FREE_BOOK_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`fb-book-tag${bookKey === opt.key ? ' selected' : ''}`}
              style={{ '--tint': opt.tint }}
              onClick={() => pickBook(opt.key)}
              aria-pressed={bookKey === opt.key}
              aria-label={opt.title}
            >
              <span className="fb-book-swatch" aria-hidden="true">
                <Icon name={opt.icon} size={15} />
              </span>
              <span className="fb-book-name">{opt.short}</span>
            </button>
          ))}
        </div>

        <button className="fa-primary-btn" disabled={!canContinue} onClick={submit}>
          Continue <span className="fa-chevron">›</span>
        </button>
      </motion.div>
    </div>
  );
};

// Scoped to `.fb-*` so nothing leaks into the two funnels that don't show this
// step. Values follow NewOnboardingKit.swift's NOTheme, which the funnel's own
// stylesheet already tracks: ink #0B1A38, subtitle #6C7A8F, accent #0A6CFF,
// accentDeep #0065F8, stroke #E8EDF5.
const FunnelBookStepStyles = () => (
  <style>{`
    /* This step must fit one screen — it has the most to say of any step in the
       funnel, and a scrollbar under a "free gift" makes the gift look like
       paperwork. Everything vertical is therefore sized in vh and clamped, so
       the layout compresses continuously instead of snapping at breakpoints.
       The card's own padding is tightened here too (see .fa-book-card). */
    .fa-book-card { padding: clamp(20px, 3.4vh, 34px) clamp(20px, 5vw, 30px) clamp(18px, 3vh, 30px); }

    /* Shell chrome, reclaimed for this step only. These are unscoped selectors,
       but this <style> element is rendered BY the book step — it exists in the
       document only while the step is mounted, so the other steps and the two
       other funnels never see them. ~60px, which is the difference between
       fitting and not on a 667px phone. */
    .fa-footer-note { display: none; }
    .fa-header { margin-bottom: clamp(8px, 1.4vh, 18px); }
    .fa-bg {
      padding-top: calc(clamp(14px, 2.4vh, 32px) + env(safe-area-inset-top, 0px));
      padding-bottom: calc(clamp(14px, 2.4vh, 32px) + env(safe-area-inset-bottom, 0px));
    }

    /* iOS onboarding is left-aligned; the funnel card is centered. Undo it for
       this step only, rather than touching .fa-center and moving every step. */
    .fb-step { text-align: left; width: 100%; }
    .fb-step .fa-eyebrow { font-size: 12px; margin-bottom: clamp(4px, 0.8vh, 8px); }
    .fb-step .fa-title {
      font-size: clamp(21px, 2.9vh + 0.6vw, 29px);
      margin-bottom: clamp(4px, 0.8vh, 9px);
    }
    .fb-step .fa-subtitle {
      font-size: clamp(13.5px, 1.7vh, 15.5px);
      line-height: 1.45;
      margin-bottom: clamp(10px, 1.8vh, 18px);
    }
    .fb-accent { color: #0065F8; }

    .fb-step button, .fb-input, .fb-story-input {
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }

    /* ── 3D hardcover mockup (hero — no iOS counterpart) ─────────────────── */
    .fb-book-stage {
      position: relative;
      display: flex; flex-direction: column; align-items: center;
      margin: 0 0 clamp(10px, 1.8vh, 20px);
      perspective: 1100px;
    }
    /* The hero is also the flex item with the most to give — it shrinks first
       and hardest as the viewport gets shorter. 0.773 keeps the cover's ratio. */
    .fb-book {
      position: relative;
      height: clamp(104px, 19vh, 188px);
      aspect-ratio: 0.773;
      transform-style: preserve-3d;
      transform: rotateY(-19deg) rotateX(5deg);
      animation: fbBookFloat 6.5s ease-in-out infinite;
    }
    /* Cream page block peeking out along the fore edge */
    .fb-book-pages {
      position: absolute; top: 4px; right: -5px; bottom: 4px; width: 10px;
      background: linear-gradient(90deg, #FFFFFF 0%, #EDEFF4 55%, #D8DDE7 100%);
      border-radius: 0 3px 3px 0;
      transform: translateZ(-4px);
    }
    .fb-book-spine {
      position: absolute; top: 0; bottom: 0; left: 0; width: 13px;
      border-radius: 7px 2px 2px 7px;
      background: linear-gradient(180deg,
        color-mix(in srgb, var(--tint) 88%, #000 12%),
        color-mix(in srgb, var(--tint) 62%, #000 38%));
      box-shadow: inset -2px 0 5px rgba(0,0,0,0.28);
    }
    .fb-book-cover {
      position: absolute; inset: 0;
      display: flex; flex-direction: column; align-items: center;
      padding: 13px 12px 12px 20px;
      box-sizing: border-box;
      border-radius: 7px 10px 10px 7px;
      overflow: hidden;
      background:
        radial-gradient(120% 90% at 50% 0%, rgba(255,255,255,0.30), transparent 62%),
        linear-gradient(160deg,
          color-mix(in srgb, var(--tint) 92%, #fff 8%),
          color-mix(in srgb, var(--tint) 64%, #0B1A38 36%));
      box-shadow:
        0 18px 34px -14px rgba(11,26,56,0.48),
        inset 0 0 0 1px rgba(255,255,255,0.22);
      transition: background 0.45s ease;
    }
    /* Slow gloss sweep — sells "printed and laminated" more than a flat fill */
    .fb-book-shine {
      position: absolute; top: -60%; left: -70%;
      width: 60%; height: 220%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.32), transparent);
      transform: rotate(18deg);
      animation: fbShine 6.5s ease-in-out infinite;
    }
    .fb-book-brand {
      font-size: 7px; font-weight: 800; letter-spacing: 0.16em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.78);
      margin-bottom: 8px;
    }
    .fb-book-hero {
      width: 52%; aspect-ratio: 1;
      object-fit: cover;
      border-radius: 50%;
      background: rgba(255,255,255,0.92);
      border: 2.5px solid rgba(255,255,255,0.92);
      box-shadow: 0 6px 16px rgba(11,26,56,0.32);
      flex-shrink: 0;
    }
    .fb-book-hero-empty {
      display: flex; align-items: center; justify-content: center;
      color: color-mix(in srgb, var(--tint) 70%, #0B1A38 30%);
    }
    .fb-book-title {
      margin-top: clamp(5px, 1vh, 10px);
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: clamp(10.5px, 1.5vh, 13px); font-weight: 700; line-height: 1.24;
      text-align: center; color: #FFFFFF;
      text-shadow: 0 1px 4px rgba(11,26,56,0.34);
      /* Long custom prompts must not push the starring line off the cover.
         flex 0 1 auto + min-height 0 is what actually lets this shrink — without
         it the title keeps its full height and overlaps the line below on a
         short cover. */
      display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
      overflow: hidden;
      flex: 0 1 auto; min-height: 0;
    }
    .fb-book-starring {
      margin-top: auto;
      flex-shrink: 0;
      font-size: 8.5px; font-weight: 600; letter-spacing: 0.05em;
      color: rgba(255,255,255,0.85);
      text-align: center;
    }
    .fb-book-shadow {
      width: clamp(84px, 15vh, 146px); height: 12px; margin-top: clamp(7px, 1.2vh, 13px);
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(11,26,56,0.24), transparent 70%);
      filter: blur(3px);
      animation: fbShadowPulse 6.5s ease-in-out infinite;
    }
    @keyframes fbBookFloat {
      0%, 100% { transform: rotateY(-19deg) rotateX(5deg) translateY(0); }
      50%      { transform: rotateY(-14deg) rotateX(3deg) translateY(-9px); }
    }
    @keyframes fbShadowPulse {
      0%, 100% { opacity: 0.85; transform: scaleX(1); }
      50%      { opacity: 0.55; transform: scaleX(0.9); }
    }
    @keyframes fbShine {
      0%, 62%, 100% { left: -70%; }
      86%           { left: 130%; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fb-book, .fb-book-shadow, .fb-book-shine { animation: none; }
    }

    /* ── write-your-own card (NOFirstAdventureStep.lessonCard) ───────────── */
    .fb-story-card {
      background: #FFFFFF;
      border: 1px solid #E8EDF5;
      border-radius: 20px;
      padding: clamp(11px, 1.6vh, 15px) 16px;
      box-shadow: 0 4px 8px rgba(0,0,0,0.05);
      transition: border-color 0.2s ease, box-shadow 0.2s ease,
                  opacity 0.3s ease, transform 0.3s ease;
    }
    .fb-story-card:focus-within {
      border-color: #0A6CFF; border-width: 2px;
      padding: calc(clamp(11px, 1.6vh, 15px) - 1px) 15px;
      box-shadow: 0 6px 18px rgba(10,108,255,0.18), 0 4px 8px rgba(0,0,0,0.05);
    }
    /* A picked ready-made owns the story — recede the card so the mode is
       unmistakable (straight from the iOS step). */
    .fb-story-card.receded { opacity: 0.5; transform: scale(0.985); }

    .fb-story-head {
      display: flex; align-items: center; gap: 6px;
      margin-bottom: clamp(5px, 0.9vh, 9px);
      color: #0A6CFF;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 12px; font-weight: 700;
      letter-spacing: 0.12em; text-transform: uppercase;
    }
    .fb-story-input-wrap { position: relative; }
    .fb-story-placeholder {
      position: absolute; inset: 0;
      font-size: clamp(14.5px, 1.9vh, 16.5px); font-weight: 500; line-height: 1.38;
      color: rgba(108,122,143,0.7);
      pointer-events: none;
      /* One line only — a wrapping placeholder would grow the card it sits in */
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .fb-story-input {
      display: block; width: 100%;
      min-height: clamp(38px, 5.2vh, 46px);
      border: none; outline: none; resize: none;
      background: transparent;
      font-family: 'Inter', sans-serif;
      font-size: clamp(14.5px, 1.9vh, 16.5px); font-weight: 500; line-height: 1.38;
      color: #0B1A38;
      padding: 0;
    }
    .fb-story-foot {
      display: flex; justify-content: flex-end;
      margin: 5px 4px 0;
      font-size: 12px; color: #A4B0C2;
    }
    .fb-story-foot .is-max { color: #FF4B4B; font-weight: 600; }

    /* ── idea + ready-made pills (NOIdeaTag / NOAdventureTag) ────────────── */
    /* One row that scrolls sideways rather than a block that wraps to three.
       Costs ~40px instead of ~130px and keeps every idea reachable. */
    .fb-idea-strip {
      display: flex; flex-wrap: nowrap; gap: 7px;
      margin-top: clamp(8px, 1.4vh, 13px);
      overflow-x: auto;
      scrollbar-width: none;
      /* Bleed to the card edge so a half-visible chip signals "more this way" */
      margin-left: -4px; margin-right: -4px;
      padding: 2px 4px;
      scroll-padding-left: 4px;
      -webkit-overflow-scrolling: touch;
    }
    .fb-idea-strip::-webkit-scrollbar { display: none; }

    .fb-idea-tag {
      display: inline-flex; align-items: center; gap: 5px;
      flex: 0 0 auto;
      white-space: nowrap;
      padding: 7px 13px;
      border-radius: 999px;
      border: 1px solid #E8EDF5;
      background: #FFFFFF;
      box-shadow: 0 2px 5px rgba(0,0,0,0.05);
      font-family: 'Inter', sans-serif;
      font-size: 13px; font-weight: 600; color: #0B1A38;
      text-align: left; cursor: pointer;
      transition: border-color 0.18s ease, background 0.18s ease,
                  color 0.18s ease, transform 0.18s ease;
    }
    .fb-idea-tag:hover { transform: translateY(-1px); border-color: #C9D8EE; }
    .fb-idea-tag:active { transform: scale(0.97); }
    .fb-idea-tag.selected {
      border-color: #0A6CFF; border-width: 1.6px;
      background: rgba(10,108,255,0.12);
      color: #0065F8;
    }
    .fb-idea-emoji { font-size: 14px; line-height: 1; }

    .fb-divider {
      display: flex; align-items: center; gap: 10px;
      margin: clamp(11px, 2vh, 20px) 0 clamp(8px, 1.4vh, 13px);
      color: #6C7A8F;
      font-size: 12.5px; font-weight: 600;
    }
    .fb-divider::before, .fb-divider::after {
      content: ''; flex: 1; height: 1px; background: #E8EDF5;
    }

    .fb-book-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }
    .fb-book-tag {
      display: flex; align-items: center; gap: 8px;
      min-width: 0;
      padding: 6px 12px 6px 6px;
      border-radius: 999px;
      border: 1px solid #E8EDF5;
      background: #FFFFFF;
      box-shadow: 0 3px 6px rgba(0,0,0,0.05);
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: clamp(12.5px, 1.7vh, 14px); font-weight: 600; color: #0B1A38;
      cursor: pointer;
      transition: transform 0.2s cubic-bezier(0.34,1.4,0.64,1),
                  box-shadow 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    }
    .fb-book-tag:hover { transform: translateY(-1px); border-color: color-mix(in srgb, var(--tint) 45%, #E8EDF5); }
    .fb-book-tag:active { transform: scale(0.97); }
    .fb-book-tag.selected {
      border-color: transparent;
      background: linear-gradient(135deg, #0A6CFF, #0065F8);
      color: #FFFFFF;
      transform: scale(1.03);
      box-shadow: 0 8px 18px rgba(10,108,255,0.32);
    }
    .fb-book-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .fb-book-swatch {
      display: flex; align-items: center; justify-content: center;
      width: clamp(26px, 3.6vh, 30px); aspect-ratio: 1; flex-shrink: 0;
      border-radius: 9px;
      color: #FFFFFF;
      background: linear-gradient(135deg,
        color-mix(in srgb, var(--tint) 92%, #fff 8%),
        color-mix(in srgb, var(--tint) 62%, #0B1A38 38%));
    }
    .fb-book-tag.selected .fb-book-swatch {
      background: rgba(255,255,255,0.22);
      box-shadow: inset 0 0 0 1px rgba(255,255,255,0.35);
    }
    .fb-step .fa-primary-btn {
      margin-top: clamp(12px, 2.2vh, 22px);
      padding: clamp(13px, 2vh, 17px) 22px;
      font-size: clamp(16px, 2.1vh, 18px);
    }

    /* Below this the clamps have given all they can, so content goes instead.
       The subtitle first (the title already says it), then the idea strip —
       the cycling placeholder teaches the same thing in zero extra height. */
    @media (max-height: 720px) {
      .fb-step .fa-subtitle { display: none; }
    }
    @media (max-height: 730px) {
      .fb-idea-strip { display: none; }
      /* Two lines is all a cover this short can hold above the starring line */
      .fb-book-title { -webkit-line-clamp: 2; }
      .fb-book-cover { padding: 10px 9px 9px 17px; }
    }
    /* Landscape phones: the hero is the only thing that can go. */
    @media (max-height: 560px) {
      .fb-book-stage { display: none; }
    }
  `}</style>
);

export default FunnelBookStep;
